/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import type { RawPayment } from "@lemmonhq/types";
import { api } from "@app/data/api";
import { useAuth } from "@app/vue/providers/Auth";
import { useTrpc } from "@app/vue/providers/index";
import { CreditNoteItemPicker } from "./CreditNoteItemPicker";
import { CreditNoteTableSection } from "./CreditNoteTableSection";
import { CreditNoteAmountSection } from "./CreditNoteAmountSection";
import { CreditNoteFooter } from "./CreditNoteFooter";
import type { CreditNoteItems, CreditNoteSubmit } from "./types";

interface CreditNoteModalProps {
  payment: RawPayment;
  /** Opened from "Reopen table": the reopen switch starts on. */
  initialReopen: boolean;
  onClose: () => void;
  onSubmit: (input: CreditNoteSubmit) => Promise<void>;
}

const BASE_METHODS = ["cash", "card", "manual"] as const;

type Mode = "items" | "amount";

/**
 * Refund / credit note for a sale: shared by "Add refund" and "Reopen table"
 * in the transaction history. The sale is never voided. "By items" credits
 * the selected items (and, for a table, can reopen it so they are owed
 * again); "Custom amount" refunds a free amount like the old refund dialog.
 * One sale cannot mix the two (the server enforces it).
 */
export const CreditNoteModal = defineComponent<CreditNoteModalProps>({
  name: "CreditNoteModal",
  props: ["payment", "initialReopen", "onClose", "onSubmit"],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    const trpc = useTrpc();
    const currency = computed(
      () => auth.user.value?.restaurant.currency || "CHF",
    );

    const loading = ref(true);
    const loadError = ref(false);
    const submitting = ref(false);
    const data = ref<CreditNoteItems>();
    const selected = ref<Record<number, number>>({});
    const reopen = ref(props.initialReopen && !!props.payment.table_id);
    const method = ref<string>(props.payment.method || "cash");
    const paidByGiftCard = ref(0);
    const paidByTableCredit = ref(0);
    const mode = ref<Mode>("items");
    const amount = ref(0);

    onMounted(async () => {
      try {
        const [items, details] = await Promise.all([
          trpc.payments.creditNoteItems.query({
            payment_id: props.payment.id,
          }),
          api.getPayment(props.payment.id).catch(() => undefined),
        ]);
        data.value = items;
        selected.value = Object.fromEntries(
          items.items.map((item) => [
            item.order_item_id,
            Number(item.available_quantity),
          ]),
        );
        if (!items.reopen_possible) reopen.value = false;
        mode.value =
          items.items.length && !items.amount_credits ? "items" : "amount";
        const rewards = details?.rewards;
        paidByTableCredit.value = Number(rewards?.table_credit_total ?? 0);
        paidByGiftCard.value =
          Number(rewards?.gift_card_total ?? 0) - paidByTableCredit.value;
      } catch (error) {
        console.error("Failed to load credit note items", error);
        loadError.value = true;
      } finally {
        loading.value = false;
      }
    });

    function hasItemCredits(d: CreditNoteItems) {
      return d.items.some((item) => Number(item.credited_quantity) > 0);
    }

    /** Items can be picked unless the sale already has an amount refund. */
    const itemsModeAvailable = computed(
      () =>
        !!data.value && data.value.items.length > 0 && !data.value.amount_credits,
    );
    /** An amount refund is impossible once items were credited. */
    const amountModeAvailable = computed(
      () => !!data.value && !hasItemCredits(data.value),
    );

    const methods = computed(() => {
      const list: string[] = [...BASE_METHODS];
      if (paidByGiftCard.value > 0) list.push("gift_card");
      if (paidByTableCredit.value > 0) list.push("table_credit");
      if (method.value && !list.includes(method.value)) list.unshift(method.value);
      return list;
    });

    const items = computed(() => data.value?.items ?? []);
    const editable = computed(() => !!data.value?.item_selection);

    const selectedLines = computed(() =>
      items.value
        .map((item) => ({
          item,
          quantity: editable.value
            ? (selected.value[item.order_item_id] ?? 0)
            : Number(item.available_quantity),
        }))
        .filter((line) => line.quantity > 0),
    );

    /** Everything still creditable is selected: credit the rest whole. */
    const isEverything = computed(() =>
      items.value.every(
        (item) =>
          !editable.value ||
          (selected.value[item.order_item_id] ?? 0) >=
            Number(item.available_quantity),
      ),
    );

    const nothingPreviouslyCredited = computed(() =>
      items.value.every((item) => Number(item.credited_quantity) === 0),
    );

    const estimatedTotal = computed(() => {
      if (!data.value) return 0;
      if (!items.value.length || (isEverything.value && !editable.value)) {
        return Number(data.value.remaining_amount);
      }
      if (isEverything.value && nothingPreviouslyCredited.value) {
        return props.payment.amount + (props.payment.tips || 0);
      }
      return selectedLines.value.reduce(
        (sum, line) => sum + line.quantity * Number(line.item.unit_price),
        0,
      );
    });

    const nothingLeft = computed(() => {
      if (!data.value) return false;
      if (mode.value === "amount") {
        return Number(data.value.remaining_amount) <= 0;
      }
      return items.value.length
        ? items.value.every((item) => Number(item.available_quantity) <= 0)
        : Number(data.value.remaining_amount) <= 0;
    });

    const canSubmit = computed(() => {
      if (loading.value || loadError.value || submitting.value) return false;
      if (nothingLeft.value) return false;
      if (mode.value === "amount") {
        return Number.isFinite(amount.value) && amount.value > 0;
      }
      return selectedLines.value.length > 0 || !items.value.length;
    });

    const isReopen = computed(() => mode.value === "items" && reopen.value);

    const handleSubmit = async () => {
      if (!canSubmit.value) return;
      submitting.value = true;
      try {
        if (mode.value === "amount") {
          await props.onSubmit({
            amount: amount.value,
            payment_method: method.value,
            reopen_table: false,
          });
          return;
        }
        await props.onSubmit({
          items:
            editable.value && !isEverything.value
              ? selectedLines.value.map((line) => ({
                  order_item_id: Number(line.item.order_item_id),
                  quantity: line.quantity,
                }))
              : undefined,
          payment_method: method.value,
          reopen_table: reopen.value,
        });
      } finally {
        submitting.value = false;
      }
    };

    const renderModeTabs = () => {
      if (itemsModeAvailable.value && amountModeAvailable.value) {
        return (
          <div role="tablist" class="tabs tabs-boxed tabs-sm">
            {(["items", "amount"] as Mode[]).map((m) => (
              <button
                key={m}
                role="tab"
                class={`tab ${mode.value === m ? "tab-active" : ""}`}
                onClick={() => (mode.value = m)}
              >
                {m === "items"
                  ? t("reports.tx.credit_note_items.mode_items")
                  : t("reports.tx.credit_note_items.mode_amount")}
              </button>
            ))}
          </div>
        );
      }
      return null;
    };

    /** Why one refund mode is unavailable, if it is. */
    const lockReason = computed(() => {
      if (!data.value) return null;
      if (itemsModeAvailable.value && amountModeAvailable.value) return null;
      if (!amountModeAvailable.value) {
        return "reports.tx.credit_note_items.amount_locked";
      }
      return data.value.amount_credits
        ? "reports.tx.credit_note_items.items_locked"
        : null;
    });

    /** One compact note: what a credit note does, plus any mode lock. */
    const renderInfo = () => (
      <div class="flex items-start gap-2 rounded-box bg-base-200/60 px-3 py-2 text-xs text-base-content/70">
        <span class="material-symbols-outlined mt-px text-base text-info">
          info
        </span>
        <div class="min-w-0 space-y-0.5">
          <p>{t("reports.tx.credit_note_items.intro")}</p>
          {lockReason.value ? (
            <p class="text-warning">{t(lockReason.value)}</p>
          ) : null}
        </div>
      </div>
    );

    const renderBody = () => {
      if (loading.value) {
        return (
          <div class="flex items-center justify-center rounded-box border border-base-300 p-4">
            <span class="loading loading-spinner loading-md"></span>
          </div>
        );
      }
      if (loadError.value || !data.value) {
        return (
          <div role="alert" class="alert alert-error bg-error/50">
            <span class="material-symbols-outlined">error</span>
            <span>{t("reports.tx.reopen_table.load_failed")}</span>
          </div>
        );
      }
      if (nothingLeft.value) {
        return (
          <div role="alert" class="alert">
            <span class="material-symbols-outlined">info</span>
            <span>{t("reports.tx.credit_note_items.nothing_left")}</span>
          </div>
        );
      }
      return (
        <>
          {renderModeTabs()}

          {mode.value === "items" &&
          props.payment.table_id &&
          data.value.reopen_possible ? (
            <CreditNoteTableSection
              tableId={props.payment.table_id}
              reopen={reopen.value}
              onToggle={(next) => (reopen.value = next)}
            />
          ) : null}

          <label class="flex items-center justify-between gap-3">
            <span class="text-sm font-medium text-accent">
              {t("general.words.payment_method")}
            </span>
            <select
              class="select select-bordered select-sm w-44 max-w-[60%]"
              value={method.value}
              onChange={(e) =>
                (method.value = (e.target as HTMLSelectElement).value)
              }
            >
              {methods.value.map((m) => (
                <option key={m} value={m}>
                  {m === "gift_card"
                    ? t("reports.tx.credit_note.gift_card")
                    : m === "table_credit"
                      ? t("reports.tx.credit_note.unused_table_credit")
                      : m.charAt(0).toUpperCase() + m.slice(1)}
                </option>
              ))}
            </select>
          </label>

          {mode.value === "amount" ? (
            <CreditNoteAmountSection
              amount={amount.value}
              remaining={Number(data.value.remaining_amount)}
              currency={currency.value}
              onChange={(next) => (amount.value = next)}
            />
          ) : null}

          {mode.value === "items" && items.value.length ? (
            <CreditNoteItemPicker
              items={items.value}
              selected={selected.value}
              editable={editable.value}
              currency={currency.value}
              onChange={(next) => (selected.value = next)}
            />
          ) : null}


        </>
      );
    };

    return () => (
      <LemmonModal
        title={t("reports.tx.credit_note.title")}
        onClose={props.onClose}
        mobileVariant="full-screen"
        renderButtons={() => (
          <CreditNoteFooter
            showTotal={
              !loading.value &&
              !loadError.value &&
              !!data.value &&
              !nothingLeft.value
            }
            total={
              mode.value === "amount"
                ? Number.isFinite(amount.value)
                  ? amount.value
                  : 0
                : estimatedTotal.value
            }
            estimated={mode.value === "items"}
            currency={currency.value}
            reopen={isReopen.value}
            submitting={submitting.value}
            canSubmit={canSubmit.value}
            onCancel={props.onClose}
            onSubmit={handleSubmit}
          />
        )}
      >
        <div class="space-y-3 p-4 sm:p-5">
          {renderInfo()}

          {renderBody()}
        </div>
      </LemmonModal>
    );
  },
});
